import React, { useState } from 'react';
import { Copy, Check, FileText } from 'lucide-react';
import { MdOutlineEdit } from 'react-icons/md';
import moment from 'moment';
import toast from 'react-hot-toast';
import './LeadInfoPanel.css';

// Source icon imports - same as leads grid
import facebook from "../../../assets/images/facebook-logo.svg";
import nineacres from "../../../assets/images/integration/99acres.png";
import excel from "../../../assets/images/integration/excel.png";
import googleforms from "../../../assets/images/integration/googleforms.png";
import housing from "../../../assets/images/integration/housing.jpg";
import indiamart from "../../../assets/images/integration/indiamart.png";
import justdial from "../../../assets/images/integration/justdial.png";
import leadform from "../../../assets/images/integration/leadform.png";
import magicbricks from "../../../assets/images/integration/magicbricks.png";
import tradeindia from "../../../assets/images/integration/tradeIndia.png";
import typeform from "../../../assets/images/integration/typeform.png";
import word from "../../../assets/images/integration/word.png";
import wordpress from "../../../assets/images/integration/wordpress.png";
import zapiuer from "../../../assets/images/integration/zapiuer.svg";
import customform from "../../../assets/images/integration/Custom Form.jpeg";
import whatsappbusiness from "../../../assets/images/whatsappbusiness.png";

// Source name → icon map (mirrors leads-renderers.tsx sourceRenderer)
const SOURCE_ICON_MAP: { [key: string]: string | JSX.Element } = {
  "99Acres": nineacres,
  CSV: excel,
  "Google spread sheets": googleforms,
  Housing: housing,
  Indiamart: indiamart,
  "Just Dial": justdial,
  "Magic Bricks": magicbricks,
  Tradeindia: tradeindia,
  "Type Form": typeform,
  Word: word,
  Wordpress: wordpress,
  Zapier: zapiuer,
  Facebook: facebook,
  "Lead Form": <FileText size={16} color="#4F46E5" />,
  "Custom Form": customform,
  "Whatsapp Business": whatsappbusiness,
};

interface LeadInfoPanelProps {
  lead: {
    name: string;
    email: string;
    phone: string;
    status: string[];
    labels: Array<{value: string; color?: string}>;
    createdAt: Date;
    leadSource: string;
    createdBy: string;
    assignedTo: string;
    list: string;
    saleValue?: string;
    leadId?: string;
  };
  onStatusEdit: () => void;
  onLabelEdit: () => void;

  onAssignClick?: () => void;
}

const LeadInfoPanel: React.FC<LeadInfoPanelProps> = ({
  lead,
  onStatusEdit,
  onLabelEdit,
  onAssignClick,
}) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = async (text: string, fieldType: 'phone' | 'email') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldType);
      toast.success(`${fieldType.charAt(0).toUpperCase() + fieldType.slice(1)} copied!`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error(`Failed to copy ${fieldType}`);
    }
  };

  return (
    <div className="lead-info-panel">
      <div className="lip-fields">
        {/* Lead ID */}
        {lead.leadId && (
          <div className="lip-field">
            <span className="lip-label">Lead ID</span>
            <div className="lip-value" style={{ fontFamily: '"Gilroy-Bold", "Gilroy", sans-serif', fontWeight: 700, letterSpacing: '0.3px' }}>{lead.leadId}</div>
          </div>
        )}

        {/* Phone */}
        {lead.phone && (
          <div className="lip-field">
            <div className="lip-field-row">
              <span className="lip-label">Phone</span>
              <button
                className={`lip-action-btn ${copiedField === 'phone' ? 'copied' : ''}`}
                onClick={() => copyToClipboard(lead.phone, 'phone')}
                title="Copy phone number"
              >
                {copiedField === 'phone' ? <Check size={12} /> : <Copy size={12} />}
              </button>
            </div>
            <div className="lip-value">{lead.phone}</div>
          </div>
        )}

        {/* Email */}
        {lead.email && (
          <div className="lip-field">
            <div className="lip-field-row">
              <span className="lip-label">Email</span>
              <button
                className={`lip-action-btn ${copiedField === 'email' ? 'copied' : ''}`}
                onClick={() => copyToClipboard(lead.email, 'email')}
                title="Copy email"
              >
                {copiedField === 'email' ? <Check size={12} /> : <Copy size={12} />}
              </button>
            </div>
            <div className="lip-value" style={{ wordBreak: 'break-all' }}>{lead.email}</div>
          </div>
        )}

        {/* Status */}
        <div className="lip-field">
          <div className="lip-field-row">
            <span className="lip-label">Status</span>
            <button className="lip-edit-btn" onClick={onStatusEdit} title="Edit status">
              <MdOutlineEdit size={11} />
            </button>
          </div>
          <div className="lip-badges">
            {lead.status?.length > 0 ? (
              lead.status.map((s, i) => (
                <span key={i} className="status-badge">{s}</span>
              ))
            ) : (
              <span className="lip-muted">No status</span>
            )}
          </div>
        </div>

        {/* Label */}
        <div className="lip-field">
          <div className="lip-field-row">
            <span className="lip-label">Label</span>
            <button className="lip-edit-btn" onClick={onLabelEdit} title="Edit labels">
              <MdOutlineEdit size={11} />
            </button>
          </div>
          <div className="lip-badges">
            {lead.labels?.length > 0 ? (
              lead.labels.map((l, i) => (
                <span key={i} className="label-badge">{l.value}</span>
              ))
            ) : (
              <span className="lip-muted">No labels</span>
            )}
          </div>
        </div>

        {/* Date Added */}
        <div className="lip-field">
          <span className="lip-label">Date added</span>
          <div className="lip-value">{moment(lead.createdAt).format("DD MMM YYYY, hh:mm A")}</div>
        </div>

        {/* Lead Source */}
        <div className="lip-field">
          <span className="lip-label">Lead Source</span>
          <div className="lip-value d-flex align-items-center" style={{ gap: '6px' }}>
            {(() => {
              const icon = SOURCE_ICON_MAP[lead.leadSource];
              if (!icon) return null;
              return typeof icon === "string"
                ? <img src={icon} alt="" style={{ width: 16, height: 16, objectFit: 'contain' }} />
                : icon;
            })()}
            <span>{lead.leadSource || "-"}</span>
          </div>
        </div>

        {/* Created By */}
        <div className="lip-field">
          <span className="lip-label">Created by</span>
          <div className="lip-value">{lead.createdBy}</div>
        </div>

        {/* Assigned To */}
        <div className="lip-field lip-clickable" onClick={onAssignClick} title="Click to reassign">
          <div className="lip-field-row">
            <span className="lip-label">Assigned to</span>
            <button className="lip-edit-btn" onClick={(e) => { e.stopPropagation(); onAssignClick?.(); }} title="Reassign lead">
              <MdOutlineEdit size={11} />
            </button>
          </div>
          <div className="lip-value">
            {lead.assignedTo && lead.assignedTo !== "-" && lead.assignedTo.trim() !== ""
              ? lead.assignedTo
              : <span className="lip-muted">Not assigned</span>
            }
          </div>
        </div>

        {/* List */}
        {lead.list && (
          <div className="lip-field">
            <span className="lip-label">List</span>
            <div className="lip-value">{lead.list}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadInfoPanel;